import { Head, Link } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft } from 'lucide-react'
import DateRangeFilter, { DateRange, getDateLabel } from '@/components/DateRangeFilter'
import AgentTreeView, { TreeAgentNode } from '@/components/AgentTreeView'

interface AgentNode extends TreeAgentNode {
  override_percentage: number
  // Absent when the organization hides commissions and equity from this viewer.
  commission_total?: number
  equity_credits?: number
  city: string | null
  state: string | null
}

interface Props extends PageProps {
  agents: AgentNode[]
  date_range: DateRange
  show_financials: boolean
}

export default function AgentTreeIndex({ agents, date_range, show_financials }: Props) {
  const totalAgents      = agents.length
  const topLevel         = agents.filter((a) => !a.parent_agent_id).length
  const subAgents        = agents.filter((a) => a.parent_agent_id).length
  const totalCommissions = agents.reduce((sum, a) => sum + (a.commission_total ?? 0), 0)
  const totalEquity      = agents.reduce((sum, a) => sum + (a.equity_credits ?? 0), 0)

  return (
    <AppLayout>
      <Head title="Agent Tree" />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/agents" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Agent Hierarchy</h1>
              <p className="text-chestnut-charcoal/70 mt-1">
                Visual tree of agent sponsorship relationships
                {date_range.preset !== 'all_time' && (
                  <span className="ml-2 text-chestnut-forest font-medium">({getDateLabel(date_range)})</span>
                )}
              </p>
            </div>
          </div>
          <DateRangeFilter dateRange={date_range} path="/admin/agent_tree" />
        </div>

        <div className={`grid gap-4 ${show_financials ? 'grid-cols-5' : 'grid-cols-3'}`}>
          <div className="card px-4 py-3 text-center">
            <p className="text-2xl font-bold text-chestnut-charcoal">{totalAgents}</p>
            <p className="text-xs text-chestnut-charcoal/60">Total Agents</p>
          </div>
          <div className="card px-4 py-3 text-center">
            <p className="text-2xl font-bold text-chestnut-forest">{topLevel}</p>
            <p className="text-xs text-chestnut-charcoal/60">Top-Level Agents</p>
          </div>
          <div className="card px-4 py-3 text-center">
            <p className="text-2xl font-bold text-chestnut-gold">{subAgents}</p>
            <p className="text-xs text-chestnut-charcoal/60">Sponsored Agents</p>
          </div>
          {show_financials && (
            <>
              <div className="card px-4 py-3 text-center">
                <p className="text-2xl font-bold text-chestnut-forest">
                  ${totalCommissions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-chestnut-charcoal/60">Total Commissions</p>
              </div>
              <div className="card px-4 py-3 text-center">
                <p className="text-2xl font-bold text-chestnut-burgundy">{totalEquity.toLocaleString()}</p>
                <p className="text-xs text-chestnut-charcoal/60">Total Equity Credits</p>
              </div>
            </>
          )}
        </div>

        <div className="card overflow-hidden" style={{ height: 'calc(100vh - 320px)', minHeight: '500px' }}>
          <AgentTreeView agents={agents} showFinancials={show_financials} defaultExpandDepth={2} />
        </div>
      </div>
    </AppLayout>
  )
}
